import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  MapPin,
  Clock,
  Users,
  Target,
  Shield,
  Zap,
  ArrowRight,
} from "lucide-react";

const AboutPage = () => {
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const values = [
    {
      icon: Target,
      title: "Operational Excellence",
      description:
        "We deliver consistent, high-quality operations that enable growth.",
    },
    {
      icon: Shield,
      title: "Reliability",
      description:
        "Dependable infrastructure you can build your business on.",
    },
    {
      icon: Zap,
      title: "Technology-Enabled",
      description:
        "Modern tools and automation for efficient operations.",
    },
    {
      icon: Users,
      title: "Partnership Approach",
      description:
        "We work as an extension of your team, not just a vendor.",
    },
  ];

  const stats = [
    { value: "50+", label: "Companies Supported" },
    { value: "15+", label: "Countries Served" },
    { value: "99.9%", label: "Operational Uptime" },
    { value: "24/7", label: "Support Available" },
  ];

  return (
    <div data-testid="about-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
                About Us
              </p>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-[#0B2A4A] mb-6 font-['Sora']">
                About VTZ Backoffice
              </h1>
              <div className="space-y-4 text-lg leading-relaxed text-slate-600">
                <p>
                  VTZ Backoffice is part of the VTZ group, a technology and operational 
                  services organization focused on supporting international businesses.
                </p>
                <p>
                  Our team combines expertise in operations, finance, technology and 
                  regulatory processes.
                </p>
                <p>
                  We provide the operational infrastructure companies need to grow 
                  efficiently across Europe.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#0B2A4A] rounded-xl p-8 lg:p-12 text-white">
                <h3 className="text-2xl font-semibold mb-8 font-['Sora']">Our Presence</h3>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-semibold">Headquarters</p>
                      <p className="text-slate-300 text-sm">Southeast Europe</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-semibold">Timezone</p>
                      <p className="text-slate-300 text-sm">CET (Central European Time)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-semibold">Focus</p>
                      <p className="text-slate-300 text-sm">International Companies</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0B2A4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white font-['Sora']">
                  {stat.value}
                </p>
                <p className="text-slate-400 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              What Drives Us
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] font-['Sora']">
              Our Core Values
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="text-center p-8 rounded-xl bg-[#F5F7FA] hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-slate-100"
              >
                <div className="w-14 h-14 bg-[#2F6FED]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-[#2F6FED]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                  {value.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VTZ Group Section */}
      <section className="py-20 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
                Part of VTZ Group
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
                A Strategic Operational Partner
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  VTZ Backoffice positions itself as a strategic operational partner, 
                  not a traditional outsourcing company. We integrate with your 
                  business to provide the operational backbone for growth.
                </p>
                <p>
                  Our team includes specialists in finance, legal, HR and technology, 
                  all working together to deliver seamless operational support.
                </p>
                <p>
                  We invest in technology and processes that enable efficient, 
                  scalable operations for international companies.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop"
                alt="Team collaboration"
                className="rounded-xl shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Let's Work Together
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Discover how VTZ Backoffice can be your operational partner for growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="about-cta-book-call"
              onClick={openCalendly}
              className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Schedule a Call
            </Button>
            <Link to="/contact">
              <Button
                data-testid="about-cta-contact"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-medium px-6 py-3 rounded-md transition-all duration-200"
              >
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
